/* eslint-disable no-case-declarations */
import moment from 'moment'
import JSZip from 'jszip'
import JSZipUtils from 'jszip-utils'

import { createHash } from 'crypto'
import { saveAs } from 'save-as'

import { getProfile } from './random-fb-profile'

const fbQuark = {
  timestamp: {
    _key: "timestamp",
    map: (_, t) => t / 1000
  },
  likeCount: 'like_count',
  likeable: 'likeable',
  liked: 'liked',
  author: {
    _key: 'author_ref',
    map: mapFbMember
  }
}

const fbReply = {
  ...fbQuark,
  content: {
    _key: 'content',
    map: mapFbText
  },
  picture: {
    _key: 'picture_ref',
    map: mapMedia
  },
  link: 'link',
  isChoice: {
    _key: 'choice_mode',
    map(_, value) {
      return value ? 1 : null
    }
  },
  choicePreview({ input = '', parent: { isChoice = false } }) {
    if (isChoice && typeof input === 'string' && input.length > 0)
      return { choice_preview: input }
  }
}

const fbAtom = {
  ...fbQuark,
  shareable: 'shareable',
  shareCount: 'share_count',
  shared: 'shared',
  caption: {
    _key: 'caption',
    map: mapFbText
  },
  commentCount: 'comment_count',
  comments: {
    '.': {
      ...fbReply,
      replyCount: 'reply_count',
      replies: {
        '.': { ...fbReply }
      }
    }
  }
}

const fbMember = {
  profilePicture: {
    _key: 'display_picture',
    map: mapMedia
  },
  coverPicture: {
    _key: 'cover_picture',
    map: mapMedia
  },
  featurePictures: {
    _key: 'pictures',
    map: mapMedia,
    array: true
  }
}

const config = {
  fb: {
    profiles: {
      _defer: true,
      '.': {
        ...fbMember,
        firstName: 'first_name',
        lastName: 'last_name',
        birthdate({ parent: { birthdate = 0, showBirthdate = false } }) {
          if (showBirthdate)
            return { 'birthdate': moment.utc(birthdate, 'YYYY-MM-DD').unix() }
        },
        relationshipStatus: 'relationship_status',
        friendCount: 'friend_count',
        isFriend: 'is_friend',
      }
    },
    pages: {
      '.': {
        ...fbMember,
        name: 'name',
        category: 'category',
        description: 'description',
        likeCount: 'like_count',
        isLiked: 'liked'
      }
    },
    groups: {
      '.': {
        ...fbMember,
        name: 'name',
        description: 'description',
        type: 'type',
        memberCount: 'member_count',
        joined: 'joined'
      }
    },
    posts: {
      '.': {
        ...fbAtom,
        ref: '_ref',
        meta: {
          _key: 'meta',
          map: mapFbText
        },
        action: {
          _key: 'action',
          map: mapFbText
        },
        sponsored: 'sponsored',
        content({ input, context }) {
          const { type = '' } = input
          let data = null

          switch (type) {
            case 'picture':
              let ref = mapMedia(context, input.src)
              let tags = (input.tags || []).map(tag => ({
                x: tag.x,
                y: tag.y,
                size: tag.size,
                route: mapFbMember(context, tag.member)
              }))
              data = {
                picture: { ref, tags }
              }
              break
            case 'share':
              data = { share_uid: input.id }
              break
            case 'link':
              data = { link: input.url, cta: input.cta }
              break
          }

          if (data)
            return {
              content_type: type,
              ...data
            }
        }
      }
    },
  },
  web(params) {
    return { web: webConfig(params) }
  }
}

function webConfig({ input, context }) {
  return Object.fromEntries(
    Object.entries(input)
      .map(([k, v]) => {
        if (k === '.') {
          v = {
            title: v.title,
            description: v.description,
            scene: v.scene,
            icon: mapMedia(context, v.icon),
            display: mapMedia(context, v.thumbnail)
          }
        } else {
          v = webConfig({ 
            key: k,
            context,
            input: v,
            parent: input
          })
        }
        return [k, v]
      })
  )
}

function stringHash(str) {
  return createHash('md5').update(str).digest('hex');
}

function mapMedia(context, url) {
  if (typeof url !== 'string' || url === '')
    return
  
  if (!(url in context.urlMap)) {
    const { mediaRoot, mediaFileExtRegex } = context.config
    const matches = url.match(mediaFileExtRegex)
    if (matches == null)
      return

    const fileExt = matches[matches.length - 1]
    const fileName = `${stringHash(url)}${fileExt}`
    context.urlMap[url] = `${mediaRoot}/${fileName}`
  }

  return context.urlMap[url]
}

// MAPPERS
const fbRandomProfileRe = /(?:@R:)(\w+)/g

function mapFbText(context, text) {
  if (typeof text !== 'string')
    return ''
  
  const matches = [...text.matchAll(fbRandomProfileRe)]
  const prefix = 'R_'

  for (let match of matches) {
    const seed = match[1]
    const id = `${prefix}${seed}`
    context.fbSeedMap[seed] = id
  }

  if (matches.length > 0)
    return text.replace(fbRandomProfileRe, `@u:${prefix}$1`)
  else
    return text
}

function mapFbMember(context, ref) {
  if (ref.length < 4)
    return ''
  
  ref = mapFbText(context, ref)

  const path = {
    u: "profiles",
    p: "pages",
    g: "groups"
  }[ref[1]]

  const id = ref.substr(3)
  return `fb://${path}/${id}`
}

// EXPORTER
function exportLeaf(context, input, config) {
  if (typeof config._key !== 'string')
    return
  
  const defer = config._defer || false
  if (!context.postProcess && defer)
    return
  
  let output = null

  if (config.array && Array.isArray(input)) {
    output = input.map(e => {
      const eOut = exportLeaf(context, e, { ...config, array: false })
      return eOut[config._key]
    })
  } else if (typeof config.map === 'function') {
    output = config.map(context, input)
  } else if (input != null) {
    output = input
  }

  if (output)
    return { [config._key]: output }
}

function exportObject(context, input, config) {
  let output = {}

  const defer = config._defer || false
  if (!context.postProcess && defer)
    return output

  for (let key in input) {
    const value = input[key]
    if (value == null)
      continue
    
    if (!('.' in config || key in config))
      continue

    let keyConfig = config[key] || config['.']
    if (keyConfig == null)
      continue

    const params = {
      key,
      context,
      input: value,
      parent: input
    }

    let keyOutput = null
    let assignOutput = true

    if (typeof keyConfig === 'string')
      keyConfig = { _key: keyConfig }

    if (typeof keyConfig === 'object') {
      if ('_key' in keyConfig) {
        keyOutput = exportLeaf(context, value, keyConfig)
      } else {
        assignOutput = false
        keyOutput = exportObject(context, value, keyConfig) || {}
      }
    } else if (typeof keyConfig === 'function') {
      keyOutput = keyConfig(params)
    }

    if (keyOutput == null)
      continue

    if (assignOutput) {
      output = Object.assign(output, keyOutput)
    } else if (keyOutput) {
      output[key] = keyOutput
    }
  }

  return output
}

function postProcess(context, input) {
  context.postProcess = true
  const seeds = Object.keys(context.fbSeedMap)

  if (seeds.length > 0) {
    if (!input.fb)
      input.fb = {}
    if (!input.fb.profiles)
      input.fb.profiles = {}

    for (let seed of seeds) {
      const id = context.fbSeedMap[seed]
      input.fb.profiles[id] = getProfile(seed, input)
    }
  }
}

export default function(input, progress) {
  return new Promise((resolve, reject) => {
    const context = {
      postProcess: false,
      urlMap: {},
      fbSeedMap: {},
      config: {
        mediaRoot: 'assets/media',
        mediaFileExtRegex: /\.(png|jpg|jpeg)/g,
        databaseRoot: 'assets/databases'
      }
    }

    progress({
      text: 'Transpiling data...',
      progress: 0
    })

    let output = exportObject(context, input, config)
    postProcess(context, input)
    output = exportObject(context, input, config)
    // TODO lol it's doing the work twice
    // Needs a pre-pass or a better post-process

    const zip = new JSZip()
    for (let dbKey in output) {
      const dest = `${context.config.databaseRoot}/${dbKey}.json`
      zip.file(dest, JSON.stringify(output[dbKey]))
    }

    const saveZipFile = () => {
      progress({
        text: 'Saving to ZIP...',
        progress: 100
      })
      return zip.generateAsync({ type: 'blob' })
        .then(content => saveAs(content, 'export.zip'))
        .then(() => resolve())
    }

    const urls = Object.keys(context.urlMap)
    let completed = 0

    for (let url of urls) {
      const dest = context.urlMap[url]
  
      JSZipUtils.getBinaryContent(url, (err, data) => {
        if (err)
          return reject(err)

        zip.file(dest, data, { binary: true })
        completed += 1
        progress({
          text: `Downloading media... (${completed} of ${urls.length})`,
          progress: Math.floor((completed / urls.length) * 100)
        })

        if (completed === urls.length)
          saveZipFile()
      })
    }

    if (urls.length === 0)
      saveZipFile()
  })
}